// components/Admin/HostProfile.tsx
//
// Host-only editor for the presenter's PUBLIC brand page (presenterProfiles/
// {uid}): name, tagline, bio, links, and the announced next show — the return
// hook players see at /p/:uid. One-time get on mount, full-blob save (see
// savePresenterProfile). Rules restrict the write to the profile's own uid.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/Auth/auth-context';
import {
  getPresenterProfile,
  savePresenterProfile,
  countFollowers,
} from '../../services/presenter';

// datetime-local wants "YYYY-MM-DDTHH:mm" in LOCAL time; ms epoch round-trips
// through these two.
const epochToLocalInput = (ms: number): string => {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const HostProfile = () => {
  const { user, userInfo } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [followers, setFollowers] = useState<number | null>(null);

  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [bio, setBio] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [youtube, setYoutube] = useState('');
  const [instagram, setInstagram] = useState('');
  const [twitter, setTwitter] = useState('');
  const [website, setWebsite] = useState('');
  const [showTitle, setShowTitle] = useState('');
  const [showWhen, setShowWhen] = useState(''); // datetime-local string
  const [showUrl, setShowUrl] = useState('');

  useEffect(() => {
    if (!user) return;
    getPresenterProfile(user)
      .then((p) => {
        // First visit: prefill the brand from the Google account.
        setName(p?.name ?? userInfo?.name ?? '');
        setTagline(p?.tagline ?? '');
        setBio(p?.bio ?? '');
        setPhotoURL(p?.photoURL ?? userInfo?.photoURL ?? '');
        setYoutube(p?.links?.youtube ?? '');
        setInstagram(p?.links?.instagram ?? '');
        setTwitter(p?.links?.twitter ?? '');
        setWebsite(p?.links?.website ?? '');
        if (p?.nextShow) {
          setShowTitle(p.nextShow.title ?? '');
          setShowWhen(p.nextShow.scheduledAt ? epochToLocalInput(p.nextShow.scheduledAt) : '');
          setShowUrl(p.nextShow.youtubeUrl ?? '');
        }
      })
      .finally(() => setLoaded(true));
    // The audience you can re-engage — players who tapped Follow. One-time
    // get of your own follows list (rules allow only you + admins).
    countFollowers(user).then(setFollowers).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    if (!name.trim()) {
      toast.error('Your page needs a name.');
      return;
    }
    if (showTitle.trim() && !showWhen) {
      toast.error('Set a date & time for the next show (or clear its title).');
      return;
    }
    setSaving(true);
    try {
      await savePresenterProfile(user, {
        name: name.trim(),
        tagline: tagline.trim() || undefined,
        bio: bio.trim() || undefined,
        photoURL: photoURL.trim() || undefined,
        links: {
          youtube: youtube.trim() || undefined,
          instagram: instagram.trim() || undefined,
          twitter: twitter.trim() || undefined,
          website: website.trim() || undefined,
        },
        nextShow: showTitle.trim()
          ? {
              title: showTitle.trim(),
              scheduledAt: new Date(showWhen).getTime(),
              ...(showUrl.trim() ? { youtubeUrl: showUrl.trim() } : {}),
            }
          : null,
      });
      toast.success('Profile published.');
    } catch {
      toast.error('Save rejected — are you signed in as a presenter?');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return <div className="max-w-2xl mx-auto p-6 text-slate-400">Loading…</div>;
  }

  const field = (label: string, node: React.ReactNode) => (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">{label}</span>
      {node}
    </label>
  );
  const inputCls =
    'w-full mt-1 p-3 rounded-xl border-2 border-ink/15 focus:border-ink focus:outline-none';

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-extrabold font-display tracking-tight">Your page</h1>
        {user && (
          <Link to={`/p/${user}`} className="btn text-sm text-ink bg-ink/10 hover:bg-ink/15">
            View public page
          </Link>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-5">
        This is your public presenter page — players can find your next live quiz here.
        Share <span className="font-semibold">quizlive → /p/your-id</span> from your streams.
      </p>

      {followers !== null && followers > 0 && (
        <div className="card p-4 mb-5 flex items-center gap-3">
          <span className="text-2xl">🔔</span>
          <p className="text-sm text-slate-600">
            <span className="font-display font-extrabold text-ink text-lg">{followers.toLocaleString()}</span>{' '}
            player{followers === 1 ? '' : 's'} follow{followers === 1 ? 's' : ''} you — they're watching this page for your next show.
          </p>
        </div>
      )}

      <div className="card p-5 space-y-4">
        {field('Name', (
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your show / brand name" />
        ))}
        {field('Tagline', (
          <input className={inputCls} value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Friday night quiz shows, live on YouTube" />
        ))}
        {field('About', (
          <textarea className={inputCls} rows={4} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell players who you are and what you host." />
        ))}
        {field('Photo URL', (
          <input className={inputCls} value={photoURL} onChange={(e) => setPhotoURL(e.target.value)} placeholder="https://…" />
        ))}
      </div>

      <h2 className="text-lg font-extrabold font-display tracking-tight mt-6 mb-2">Links</h2>
      <div className="card p-5 grid sm:grid-cols-2 gap-4">
        {field('YouTube channel', (
          <input className={inputCls} value={youtube} onChange={(e) => setYoutube(e.target.value)} placeholder="https://youtube.com/@…" />
        ))}
        {field('Instagram', (
          <input className={inputCls} value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="https://instagram.com/…" />
        ))}
        {field('X / Twitter', (
          <input className={inputCls} value={twitter} onChange={(e) => setTwitter(e.target.value)} placeholder="https://x.com/…" />
        ))}
        {field('Website', (
          <input className={inputCls} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" />
        ))}
      </div>

      <h2 className="text-lg font-extrabold font-display tracking-tight mt-6 mb-1">Next live quiz</h2>
      <p className="text-sm text-slate-500 mb-2">
        Announce your next show — it's the headline of your public page. Clear the title to unannounce.
      </p>
      <div className="card p-5 space-y-4">
        {field('Show title', (
          <input className={inputCls} value={showTitle} onChange={(e) => setShowTitle(e.target.value)} placeholder="Fun Friday #4 — Bollywood edition" />
        ))}
        <div className="grid sm:grid-cols-2 gap-4">
          {field('When', (
            <input type="datetime-local" className={inputCls} value={showWhen} onChange={(e) => setShowWhen(e.target.value)} />
          ))}
          {field('YouTube link (optional)', (
            <input className={inputCls} value={showUrl} onChange={(e) => setShowUrl(e.target.value)} placeholder="https://youtube.com/live/…" />
          ))}
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} className="btn-primary w-full mt-6">
        {saving ? 'Publishing…' : 'Publish profile'}
      </button>
    </div>
  );
};
